/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { PlusCircleIcon, TrashIcon } from '@heroicons/react/solid'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { useUpdateCartItemsMutation } from 'api/shop'
import { Weight } from 'models/enums'
import { CartAddon } from 'models/shop'
import { useAppSelector } from 'store/hooks'
import { aud } from 'helpers/utils'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    addons: CartAddon[]
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function Addons({ addons }: Props) {
    const { pathname } = useLocation()
    const { cart } = useAppSelector((state) => state.shop)
    const [updateCartItems] = useUpdateCartItemsMutation()

    return (
        <StyledAddons>
            {addons.map(({ name, price, item_uuid }: CartAddon) => {
                return (
                    <AddonWrapper key={name}>
                        <Name>
                            <Icon>
                                <PlusCircleIcon/>
                            </Icon>
                            <div>
                                {name}
                            </div>
                        </Name>
                        {pathname === '/order'
                            ? (
                                <Actions>
                                    <div>{aud(price.total)}</div>
                                </Actions>
                            )
                            : (
                                <Actions>
                                    <div>{aud(price.total)}</div>
                                    <Action>
                                        <button
                                            type={'button'}
                                            onClick={() => {
                                                if (cart && cart.uuid) {
                                                    updateCartItems({ uuid: cart.uuid, remove_items: [item_uuid] })
                                                }
                                            }}
                                        >
                                            <Trash>
                                                <TrashIcon/>
                                            </Trash>
                                        </button>
                                    </Action>
                                </Actions>
                            )
                        }
                    </AddonWrapper>
                )
            })}
        </StyledAddons>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const StyledAddons = styled.div`
    display: grid;
    gap: 16px;
    padding-top: 12px;
    padding-bottom: 16px;
    font-weight: ${Weight.Medium};
`

const AddonWrapper = styled.div`
    display: flex;
    justify-content: space-between;
`

const Name = styled.div`
    display: flex;
    align-items: center;
    padding-left: 12px;
    gap: 12px;
`

const Actions = styled.div`
    display: flex;
    gap: 16px;
`

const Action = styled.div`
    display: grid;
`

const Icon = styled.div`
    > svg {
        width: 16px;
        height: 16px;
        color: ${props => props.theme['icon--100']}
    }
`

const Trash = styled.div`
    > svg {
        width: 16px;
        height: 16px;
        color: ${props => props.theme['error--100']}
    }
`
